// src/pages/Admin/index.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Tabs, Table, Button, Modal, Form, Input, Select,
  Space, Card, Typography, message, Tag, Switch,
} from 'antd';
import {
  PlusOutlined, SettingOutlined, AppstoreOutlined,
  EnvironmentOutlined, BankOutlined, SyncOutlined,
  UserOutlined, LockOutlined,
} from '@ant-design/icons';
import { dictAPI, syncAPI, actsAPI } from '../../api/client';
import PermissionsTab from './PermissionsTab';

const { Title, Text } = Typography;

function CategoriesTab() {
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['categories'], queryFn: () => dictAPI.getCategories().then(r => r.data) });
  const createMutation = useMutation({
    mutationFn: (data) => dictAPI.createCategory(data),
    onSuccess: () => { message.success('Категория добавлена'); setShowModal(false); form.resetFields(); queryClient.invalidateQueries(['categories']); },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });
  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">Категории оборудования</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setShowModal(true)}>Добавить</Button>
      </div>
      <Table dataSource={data || []} rowKey="id" size="small" loading={isLoading} pagination={false}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 60 },
          { title: 'Название', dataIndex: 'name' },
        ]}
      />
      <Modal open={showModal} title="Новая категория"
        onOk={() => form.validateFields().then(v => createMutation.mutate(v))}
        onCancel={() => { setShowModal(false); form.resetFields(); }}
        confirmLoading={createMutation.isPending} okText="Добавить" cancelText="Отмена">
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input placeholder="Оборудование рабочих мест" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function TypesTab() {
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: () => dictAPI.getCategories().then(r => r.data) });
  const { data, isLoading } = useQuery({ queryKey: ['types'], queryFn: () => dictAPI.getTypes().then(r => r.data) });
  const createMutation = useMutation({
    mutationFn: (data) => dictAPI.createType(data),
    onSuccess: () => { message.success('Тип добавлен'); setShowModal(false); form.resetFields(); queryClient.invalidateQueries(['types']); },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });
  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">Типы оборудования</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setShowModal(true)}>Добавить</Button>
      </div>
      <Table dataSource={data || []} rowKey="id" size="small" loading={isLoading}
        pagination={{ pageSize: 20, showTotal: (t) => `${t} типов` }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 60 },
          { title: 'Категория', dataIndex: 'category', width: 220, render: (v) => <Tag>{v}</Tag> },
          { title: 'Тип', dataIndex: 'name' },
        ]}
      />
      <Modal open={showModal} title="Новый тип"
        onOk={() => form.validateFields().then(v => createMutation.mutate(v))}
        onCancel={() => { setShowModal(false); form.resetFields(); }}
        confirmLoading={createMutation.isPending} okText="Добавить" cancelText="Отмена">
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="category_id" label="Категория" rules={[{ required: true }]}>
            <Select placeholder="Выберите категорию"
              options={(categories || []).map(c => ({ label: c.name, value: c.id }))} />
          </Form.Item>
          <Form.Item name="name" label="Название типа" rules={[{ required: true }]}>
            <Input placeholder="Ноутбук, Монитор..." />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function LocationsTab() {
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['locations'], queryFn: () => dictAPI.getLocations().then(r => r.data) });
  const createMutation = useMutation({
    mutationFn: (data) => dictAPI.createLocation(data),
    onSuccess: () => { message.success('Локация добавлена'); setShowModal(false); form.resetFields(); queryClient.invalidateQueries(['locations']); },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });
  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">Места хранения</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setShowModal(true)}>Добавить</Button>
      </div>
      <Table dataSource={data || []} rowKey="id" size="small" loading={isLoading} pagination={false}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 60 },
          { title: 'Локация', dataIndex: 'name' },
        ]}
      />
      <Modal open={showModal} title="Новая локация"
        onOk={() => form.validateFields().then(v => createMutation.mutate(v))}
        onCancel={() => { setShowModal(false); form.resetFields(); }}
        confirmLoading={createMutation.isPending} okText="Добавить" cancelText="Отмена">
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input placeholder="Склад СПб..." />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function CompaniesTab() {
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['companies'], queryFn: () => dictAPI.getCompanies().then(r => r.data) });
  const createMutation = useMutation({
    mutationFn: (data) => dictAPI.createCompany(data),
    onSuccess: () => { message.success('Компания добавлена'); setShowModal(false); form.resetFields(); queryClient.invalidateQueries(['companies']); },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });
  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">Юридические лица</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setShowModal(true)}>Добавить</Button>
      </div>
      <Table dataSource={data || []} rowKey="id" size="small" loading={isLoading} pagination={false}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 60 },
          { title: 'Название', dataIndex: 'name' },
          {
            title: 'Tribune ID', dataIndex: 'tribune_legal_entity_id',
            render: (v) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : '—',
          },
        ]}
      />
      <Modal open={showModal} title="Новая компания"
        onOk={() => form.validateFields().then(v => createMutation.mutate(v))}
        onCancel={() => { setShowModal(false); form.resetFields(); }}
        confirmLoading={createMutation.isPending} okText="Добавить" cancelText="Отмена">
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input placeholder="ООО Компания" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function SignersTab() {
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: companies } = useQuery({ queryKey: ['companies'], queryFn: () => dictAPI.getCompanies().then(r => r.data) });
  const { data, isLoading } = useQuery({ queryKey: ['signers-all'], queryFn: () => actsAPI.getSigners().then(r => r.data) });

  const invalidate = () => {
    queryClient.invalidateQueries(['signers-all']);
    queryClient.invalidateQueries(['signers']);
  };

  const createMutation = useMutation({
    mutationFn: (data) => actsAPI.createSigner(data),
    onSuccess: () => { message.success('Подписант добавлен'); setShowModal(false); setEditItem(null); form.resetFields(); invalidate(); },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => actsAPI.updateSigner(id, data),
    onSuccess: () => { message.success('Сохранено'); setShowModal(false); setEditItem(null); form.resetFields(); invalidate(); },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }) => actsAPI.updateSigner(id, { is_active }),
    onSuccess: (_, vars) => { message.success(vars.is_active ? 'Активирован' : 'Деактивирован'); invalidate(); },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const openEdit = (record) => {
    setEditItem(record);
    form.setFieldsValue({
      company_id:      companies?.find(c => c.name === record.company_name)?.id,
      name_genitive:   record.name_genitive,
      name_nominative: record.name_nominative,
      basis:           record.basis,
      is_active:       record.is_active,
    });
    setShowModal(true);
  };

  const openAdd = () => { setEditItem(null); form.resetFields(); form.setFieldsValue({ is_active: true }); setShowModal(true); };

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">Уполномоченные подписывать акты</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>Добавить</Button>
      </div>
      <Table dataSource={data || []} rowKey="id" size="small" loading={isLoading} pagination={false}
        columns={[
          { title: 'Компания', dataIndex: 'company_name', width: 160, render: (v) => <Tag>{v}</Tag> },
          { title: 'ФИО (именит.)', dataIndex: 'name_nominative', width: 220, render: (v) => <Text strong>{v}</Text> },
          { title: 'ФИО (родит.)', dataIndex: 'name_genitive', width: 220, render: (v) => <Text type="secondary">{v}</Text> },
          { title: 'Основание', dataIndex: 'basis', ellipsis: true },
          {
            title: 'Активен', dataIndex: 'is_active', width: 90, align: 'center',
            render: (v, r) => (
              <Switch size="small" checked={v} checkedChildren="Да" unCheckedChildren="Нет"
                loading={toggleActiveMutation.isPending}
                onChange={(checked) => toggleActiveMutation.mutate({ id: r.id, is_active: checked })} />
            ),
          },
          { title: '', width: 48, render: (_, r) => <Button type="text" size="small" onClick={() => openEdit(r)}>✏️</Button> },
        ]}
      />
      <Modal open={showModal} title={editItem ? 'Редактировать подписанта' : 'Новый подписант'}
        onOk={() => form.validateFields().then(values => editItem ? updateMutation.mutate({ id: editItem.id, data: values }) : createMutation.mutate(values))}
        onCancel={() => { setShowModal(false); setEditItem(null); form.resetFields(); }}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okText={editItem ? 'Сохранить' : 'Добавить'} cancelText="Отмена" width={560}>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="company_id" label="Юр. лицо" rules={[{ required: true }]}>
            <Select placeholder="Выберите компанию" options={(companies || []).map(c => ({ label: c.name, value: c.id }))} />
          </Form.Item>
          <Form.Item name="name_nominative" label="ФИО (именительный)" rules={[{ required: true }]} extra="Пример: Лазутин Александр Александрович">
            <Input placeholder="Иванов Иван Иванович" />
          </Form.Item>
          <Form.Item name="name_genitive" label="ФИО (родительный)" rules={[{ required: true }]} extra="Пример: Лазутина Александра Александровича">
            <Input placeholder="Иванова Ивана Ивановича" />
          </Form.Item>
          <Form.Item name="basis" label="Основание" rules={[{ required: true }]}>
            <Input placeholder="Доверенности №... от ..." />
          </Form.Item>
          <Form.Item name="is_active" label="Активен" valuePropName="checked">
            <Switch checkedChildren="Да" unCheckedChildren="Нет" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default function AdminPage() {
  const [syncing, setSyncing] = useState(false);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>Админка</Title>
      </div>

      <Card size="small" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text strong>Tribune — синхронизация сотрудников</Text>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Автоматически каждый час. Дополняет данные, не перетирает ручные правки.
              </Text>
            </div>
          </div>
          <Button type="primary" icon={<SyncOutlined />} loading={syncing}
            onClick={async () => {
              setSyncing(true);
              try {
                const res = await syncAPI.runTribune();
                message.success(`Синк завершён: +${res.data.added} новых, ~${res.data.updated} обновлено`);
              } catch (e) {
                message.error('Ошибка синка Tribune');
              } finally {
                setSyncing(false);
              }
            }}>
            Синк Tribune
          </Button>
        </div>
      </Card>

      <Card size="small">
        <Tabs defaultActiveKey="access" items={[
          {
            key: 'access',
            label: <Space><LockOutlined />Доступы</Space>,
            children: <PermissionsTab />,
          },
          {
            key: 'categories',
            label: <Space><AppstoreOutlined />Категории</Space>,
            children: <CategoriesTab />,
          },
          {
            key: 'types',
            label: <Space><SettingOutlined />Типы</Space>,
            children: <TypesTab />,
          },
          {
            key: 'locations',
            label: <Space><EnvironmentOutlined />Локации</Space>,
            children: <LocationsTab />,
          },
          {
            key: 'companies',
            label: <Space><BankOutlined />Компании</Space>,
            children: <CompaniesTab />,
          },
          {
            key: 'signers',
            label: <Space><UserOutlined />Подписанты</Space>,
            children: <SignersTab />,
          },
        ]} />
      </Card>
    </div>
  );
}